import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FishBoneCause} from '../types/project-form';

// Scored fields in the prioritization table and their display labels
const SCORED_FIELDS: Array<{ field: 'impact' | 'evidence' | 'feasibility'; label: string; options: Record<number, string> }> = [
  {
    field: 'impact',
    label: 'Urgency',
    options: { 3: '3 - Very Urgent', 2: '2 - Urgent', 1: '1 - Not Urgent' }
  },
  {
    field: 'evidence',
    label: 'Cost',
    options: { 3: '3 - Low Cost', 2: '2 - Moderate Cost', 1: '1 - High Cost' }
  },
  {
    field: 'feasibility',
    label: 'Feasibility',
    options: { 3: '3 - Very easy to implement', 2: '2 - Manageable to implement', 1: '1 - Difficult to implement' }
  }
];

interface FishBoneCauseSectionProps {
  title: string;
  description: string;
  categoryKey: 'peopleCauses' | 'processCauses' | 'policyCauses' | 'placeCauses' | 'provisionCauses';
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  addButtonText: string;
}

const FishBoneCauseSection: React.FC<FishBoneCauseSectionProps> = ({
  title,
  description,
  categoryKey,
  formData,
  setFormData,
  addButtonText
}) => {
  const categoryData = formData.fishBoneAnalysis?.[categoryKey];
  const causeItems = categoryData?.causeItems || [];

  // Tracks a cell whose value was auto-cleared so it can be highlighted
  const [clearedCell, setClearedCell] = useState<{ causeId: string; field: string } | null>(null);
  const [notice, setNotice] = useState<string>('');
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  }, []);

  const updateCauseItem = (index: number, updates: Partial<FishBoneCause>) => {
    const updatedItems = causeItems.map((c: FishBoneCause) => ({ ...c }));

    // A value may only be assigned once per scored field - clear it from any
    // other cause that already uses it and collect details for the notification
    const cleared: Array<{ cause: FishBoneCause; field: string; label: string; optionLabel: string }> = [];
    SCORED_FIELDS.forEach(({ field, label, options }) => {
      if (field in updates) {
        const newValue = updates[field] as number;
        const conflict = updatedItems.find((c: FishBoneCause, i: number) => i !== index && c[field] === newValue);
        if (conflict) {
          cleared.push({
            cause: conflict,
            field,
            label,
            optionLabel: options[newValue] ?? String(newValue)
          });
          conflict[field] = undefined;
        }
      }
    });

    updatedItems[index] = { ...updatedItems[index], ...updates };

    // Recalculate totals for every item (a cleared item's total also changes)
    updatedItems.forEach((item: FishBoneCause) => {
      item.total = (item.impact || 0) + (item.evidence || 0) + (item.feasibility || 0);
    });

    // Auto-calculate ranks only when all causes are fully scored
    const allScored = updatedItems.length > 0 && updatedItems.every((item: FishBoneCause) => 
      item.total && item.total > 0
    );
    
    let finalItems = updatedItems;
    if (allScored) {
      // Calculate ranks when all causes are scored
      finalItems = updatedItems.map((item: FishBoneCause) => ({ ...item }))
        .sort((a: FishBoneCause, b: FishBoneCause) => (b.total || 0) - (a.total || 0))
        .map((item: FishBoneCause, idx: number) => ({ ...item, rank: idx + 1 }));
    }

    setFormData((prev: any) => {
      const fishBoneAnalysis = prev.fishBoneAnalysis || {
        peopleCauses: { causes: '', causeItems: [] },
        processCauses: { causes: '', causeItems: [] },
        policyCauses: { causes: '', causeItems: [] },
        placeCauses: { causes: '', causeItems: [] },
        provisionCauses: { causes: '', causeItems: [] }
      };
      const categoryData = fishBoneAnalysis[categoryKey] || { causes: '', causeItems: [] };

      return {
        ...prev,
        fishBoneAnalysis: {
          ...fishBoneAnalysis,
          [categoryKey]: {
            ...categoryData,
            causeItems: finalItems
          }
        }
      };
    });

    // Notify the user and highlight the cell that was cleared
    if (cleared.length > 0) {
      const first = cleared[0];
      const currentCause = causeItems[index];
      setClearedCell({ causeId: first.cause.id, field: first.field });
      setNotice(
        `"${first.optionLabel}" under ${first.label} was cleared for "${first.cause.text || 'another cause'}" because the same choice was selected for "${currentCause?.text || 'this cause'}".`
      );

      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => setClearedCell(null), 6000);
    }
  };


  const addCause = () => {
    if (causeItems.length >= 3) return;

    const newCause: FishBoneCause = {
      id: crypto.randomUUID(),
      text: ''
    };

    const updatedItems = [...causeItems, newCause];

    setFormData((prev: any) => {
      // Initialize fishBoneAnalysis if it doesn't exist
      const fishBoneAnalysis = prev.fishBoneAnalysis || {
        peopleCauses: { causes: '', causeItems: [] },
        processCauses: { causes: '', causeItems: [] },
        policyCauses: { causes: '', causeItems: [] },
        placeCauses: { causes: '', causeItems: [] },
        provisionCauses: { causes: '', causeItems: [] }
      };

      // Initialize category if it doesn't exist
      const categoryData = fishBoneAnalysis[categoryKey] || { causes: '', causeItems: [] };

      return {
        ...prev,
        fishBoneAnalysis: {
          ...fishBoneAnalysis,
          [categoryKey]: {
            ...categoryData,
            causeItems: updatedItems
          }
        }
      };
    });
  };

  const removeCause = (index: number) => {
    const updatedItems = causeItems.filter((_: any, i: number) => i !== index);

    setFormData((prev: any) => {
      const fishBoneAnalysis = prev.fishBoneAnalysis || {
        peopleCauses: { causes: '', causeItems: [] },
        processCauses: { causes: '', causeItems: [] },
        policyCauses: { causes: '', causeItems: [] },
        placeCauses: { causes: '', causeItems: [] },
        provisionCauses: { causes: '', causeItems: [] }
      };
      const categoryData = fishBoneAnalysis[categoryKey] || { causes: '', causeItems: [] };

      return {
        ...prev,
        fishBoneAnalysis: {
          ...fishBoneAnalysis,
          [categoryKey]: {
            ...categoryData,
            causeItems: updatedItems
          }
        }
      };
    });
  };

  const updateWhyAnalysis = (whyLevel: string, value: string) => {
    const updatedItems = causeItems.map((cause: FishBoneCause) => {
      if (cause.rank === 1) {
        const updatedWhyAnalysis = {
          ...(cause.whyAnalysis || {}),
          [whyLevel]: value
        };
        
        // Set root cause if this is why5
        if (whyLevel === 'why5') {
          updatedWhyAnalysis.rootCause = value;
        }
        
        return { 
          ...cause, 
          whyAnalysis: updatedWhyAnalysis
        };
      }
      return cause;
    });

    setFormData((prev: any) => {
      const fishBoneAnalysis = prev.fishBoneAnalysis || {
        peopleCauses: { causes: '', causeItems: [] },
        processCauses: { causes: '', causeItems: [] },
        policyCauses: { causes: '', causeItems: [] },
        placeCauses: { causes: '', causeItems: [] },
        provisionCauses: { causes: '', causeItems: [] }
      };
      const categoryData = fishBoneAnalysis[categoryKey] || { causes: '', causeItems: [] };

      return {
        ...prev,
        fishBoneAnalysis: {
          ...fishBoneAnalysis,
          [categoryKey]: {
            ...categoryData,
            causeItems: updatedItems
          }
        }
      };
    });
  };

  const topRankedCause = causeItems.find((c: FishBoneCause) => c.rank === 1);
  
  // Check if all causes have been scored (have totals)
  const allCausesScored = causeItems.length > 0 && causeItems.every((cause: FishBoneCause) => 
    cause.total && cause.total > 0
  );
  
  // Check if rankings have been calculated
  const rankingsCalculated = causeItems.some((cause: FishBoneCause) => cause.rank);

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {/* Individual Items */}
      <Box>
        {causeItems.map((cause: FishBoneCause, index: number) => (
          <Box key={cause.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={cause.text}
              label={`Cause ${index + 1}`}
              onChange={(e) => updateCauseItem(index, { text: e.target.value })}
              sx={{ mr: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => removeCause(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={addCause}
          sx={{ mt: 1 }}
          disabled={causeItems.length >= 3}
        >
          {addButtonText}
        </Button>
      </Box>

      {/* Enhanced Ranking Table */}
      {causeItems.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
            Ranking Table for {title}
          </Typography>
          
          {/* Column Guide */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
              Column Guide:
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              • <strong>Urgency:</strong> identified gap needs immediate attention?
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              • <strong>Cost:</strong> financial needs versus availability of a budget
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • <strong>Feasibility:</strong> how easy is it to do/implement 
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Cause</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Urgency of the identified gap</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Cost of resolving the identified gap</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Feasibility</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>Total</TableCell>
                  {rankingsCalculated && (
                    <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>Rank</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {causeItems.map((cause: FishBoneCause, index: number) => (
                  <TableRow key={cause.id}>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {cause.text || `Cause ${index + 1}`}
                    </TableCell>
                    {SCORED_FIELDS.map(({ field, options }) => (
                      <TableCell
                        key={field}
                        align="center"
                        sx={{
                          transition: 'background-color 0.4s',
                          ...(clearedCell &&
                            clearedCell.causeId === cause.id &&
                            clearedCell.field === field
                              ? { bgcolor: 'warning.light' }
                              : {})
                        }}
                      >
                        <Select
                          value={cause[field] || ''}
                          onChange={(e) => updateCauseItem(index, { [field]: Number(e.target.value) })}
                          sx={{
                            minWidth: 100,
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            }
                          }}
                        >
                          {[3, 2, 1].map((value) => (
                            <MenuItem key={value} value={value}>
                              {options[value]}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 40,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: cause.total ? 'primary.100' : 'grey.100',
                          color: cause.total ? 'primary.800' : 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}
                      >
                        {cause.total || 0}
                      </Box>
                    </TableCell>
                    {rankingsCalculated && (
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: cause.rank === 1 ? 'success.main' : cause.rank === 2 ? 'warning.main' : cause.rank === 3 ? 'info.main' : 'grey.300',
                            color: cause.rank && cause.rank <= 3 ? 'white' : 'text.secondary',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }}
                        >
                          {cause.rank || '-'}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Show message when not all causes are scored */}
          {causeItems.length > 0 && !allCausesScored && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Complete scoring for all causes to see rankings.
            </Alert>
          )}
          
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
          Add at least one cause above to begin ranking.
        </Alert>
      )}

      {/* 5-Why Analysis for highest ranked cause */}
      {topRankedCause && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            5-Why Analysis for Highest Ranked Cause
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            For the highest ranked cause, complete the 5-Why analysis to identify the root cause.
          </Typography>
          
          {/* Why 1 */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label={`Why 1: Why "${topRankedCause.text || 'this cause'}"?`}
            size="small"
            sx={{ mb: 2 }}
            value={topRankedCause.whyAnalysis?.why1 || ''}
            onChange={(e) => updateWhyAnalysis('why1', e.target.value)}
          />
          
          {/* Why 2 - Only show if Why 1 has content */}
          {topRankedCause.whyAnalysis?.why1?.trim() && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label={`Why 2: Why ${topRankedCause.whyAnalysis.why1.trim() || 'that'}?`}
              size="small"
              sx={{ mb: 2 }}
              value={topRankedCause.whyAnalysis?.why2 || ''}
              onChange={(e) => updateWhyAnalysis('why2', e.target.value)}
            />
          )}
          
          {/* Why 3 - Only show if Why 2 has content */}
          {topRankedCause.whyAnalysis?.why2?.trim() && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label={`Why 3: Why ${topRankedCause.whyAnalysis.why2.trim() || 'that'}?`}
              size="small"
              sx={{ mb: 2 }}
              value={topRankedCause.whyAnalysis?.why3 || ''}
              onChange={(e) => updateWhyAnalysis('why3', e.target.value)}
            />
          )}
          
          {/* Why 4 - Only show if Why 3 has content */}
          {topRankedCause.whyAnalysis?.why3?.trim() && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label={`Why 4: Why ${topRankedCause.whyAnalysis.why3.trim() || 'that'}?`}
              size="small"
              sx={{ mb: 2 }}
              value={topRankedCause.whyAnalysis?.why4 || ''}
              onChange={(e) => updateWhyAnalysis('why4', e.target.value)}
            />
          )}
          
          {/* Why 5 - Only show if Why 4 has content */}
          {topRankedCause.whyAnalysis?.why4?.trim() && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label={`Why 5: Why ${topRankedCause.whyAnalysis.why4.trim() || 'that'}?`}
              size="small"
              sx={{ mb: 2 }}
              value={topRankedCause.whyAnalysis?.why5 || ''}
              onChange={(e) => updateWhyAnalysis('why5', e.target.value)}
            />
          )}
          
          {/* Root Cause Summary */}
          {topRankedCause.whyAnalysis?.rootCause && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Identified Root Cause:
              </Typography>
              <Typography>
                {topRankedCause.whyAnalysis.rootCause}
              </Typography>
            </Paper>
          )}
        </>
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setNotice('')}>
          {notice}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default FishBoneCauseSection;
